import {
  fetchVistaProjects,
  fetchVistaRubrics,
  fetchVistaAspects,
} from './vistaJudging';
import {
  categoryToVistaMapping,
  CURRENT_AWARD_YEAR,
} from 'constants/evaluation';
import { CATEGORIES_BY_YEAR } from 'constants/categoryNames';

export interface AspectMapping {
  documentId: string;
  name: string;
  description: string;
  weight: number;
  scaleMin: number;
  scaleMax: number;
  /** Score → description map from Strapi, e.g. { "1": "...", "10": "..." } */
  scaleGuidance: Record<string, string>;
}

export interface RubricMapping {
  documentId: string;
  name: string;
  aspects: AspectMapping[];
}

export interface CategoryMapping {
  categoryName: string; // e.g. "Innovation Excellence Award"
  vistaProjectName: string; // e.g. "INNOVATION EXCELLENCE AWARD"
  projectDocumentId: string;
  rubric: RubricMapping | null;
}

/**
 * Fetches all vista-projects, vista-rubrics, and vista-aspects from Strapi
 * and assembles a structured Category → Rubric → Aspects mapping.
 */
export const fetchCategoryRubricMapping = async (
  year: number = CURRENT_AWARD_YEAR
): Promise<CategoryMapping[]> => {
  // Fetch all three endpoints in parallel
  const [projectsResponse, rubricsResponse, aspectsResponse] =
    await Promise.all([
      fetchVistaProjects(year),
      fetchVistaRubrics(year),
      fetchVistaAspects(year),
    ]);

  const projects: any[] = projectsResponse.data || [];
  const rubrics: any[] = rubricsResponse.data || [];
  const aspects: any[] = aspectsResponse.data || [];

  // Build an index of aspects by documentId for quick lookup
  const aspectIndex = new Map<string, any>(aspects.map(a => [a.documentId, a]));

  // Build an index of rubrics by documentId for quick lookup
  const rubricIndex = new Map<string, any>(rubrics.map(r => [r.documentId, r]));

  // Build an index of projects by vista name for quick lookup
  const projectsByVistaName = new Map<string, any>(
    projects.map(p => [p.name, p])
  );

  // Determine which categories belong to this specific year
  const yearCategories =
    CATEGORIES_BY_YEAR[year] || CATEGORIES_BY_YEAR[CURRENT_AWARD_YEAR] || [];

  // For each award category, find the matching project, rubric, and aspects
  const mappings: CategoryMapping[] = yearCategories.map(categoryName => {
    const vistaName = categoryToVistaMapping(categoryName);
    const project = projectsByVistaName.get(vistaName);

    if (!project) {
      return {
        categoryName,
        vistaProjectName: vistaName,
        projectDocumentId: '',
        rubric: null,
      };
    }

    const rubric = project.rubric_document_id
      ? rubricIndex.get(project.rubric_document_id)
      : null;

    let rubricMapping: RubricMapping | null = null;

    console.log(rubric, 'rubric');
    if (rubric) {
      const aspectList: any[] = Array.isArray(rubric.aspect_list)
        ? rubric.aspect_list
        : [];

      const enrichedAspects: AspectMapping[] = aspectList
        .map((item: any) => {
          const aspect = aspectIndex.get(item.aspect_id);
          if (!aspect) return null;

          // Normalise scale_guidance: Strapi may store it as an object or array
          const rawGuidance = aspect.scale_guidance ?? {};
          const scaleGuidance: Record<string, string> = {};
          if (Array.isArray(rawGuidance)) {
            // Array form: [{ score, description }]
            rawGuidance.forEach((g: any) => {
              scaleGuidance[String(g.score ?? g.key)] =
                g.description ?? g.value ?? '';
            });
          } else if (typeof rawGuidance === 'object') {
            // Object form: { "1": "...", "10": "..." }
            Object.entries(rawGuidance).forEach(([k, v]) => {
              scaleGuidance[k] = String(v);
            });
          }

          return {
            documentId: aspect.documentId,
            name: aspect.name,
            description: aspect.description || '',
            weight: item.weight ?? aspect.weight ?? 0,
            scaleMin: aspect.scale_min ?? 0,
            scaleMax: aspect.scale_max ?? 10,
            scaleGuidance,
          };
        })
        .filter((a): a is AspectMapping => a !== null);

      rubricMapping = {
        documentId: rubric.documentId,
        name: rubric.name,
        aspects: enrichedAspects,
      };
    }

    return {
      categoryName,
      vistaProjectName: vistaName,
      projectDocumentId: project.documentId,
      rubric: rubricMapping,
    };
  });

  return mappings;
};
